import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../utils/api';
import { LogoFull } from './Logo';
import PwEyeIcon from './PwEyeIcon';
import { StoreBadge, APP_STORE_URL, PLAY_STORE_URL } from './LandingPage';
import { T } from '../v2/theme';
import { koJosa } from '../v2/malso/koJosa';

function Register({ onLogin }) {
  var navigate = useNavigate();
  var [form, setForm] = useState({ userId: '', password: '', passwordConfirm: '', companyName: '', companyNameEn: '', ceo: '', tel: '', bizNo: '', email: '', companyType: 'user', purpose: '' });
  var [agreeTerms, setAgreeTerms] = useState(false);
  var [agreePrivacy, setAgreePrivacy] = useState(false);
  var [honeypot, setHoneypot] = useState('');
  var [error, setError] = useState('');
  var [success, setSuccess] = useState('');
  var [loading, setLoading] = useState(false);
  var [idCheck, setIdCheck] = useState(null);
  var [showPw, setShowPw] = useState(false);
  var [bizFile, setBizFile] = useState(null);
  var [bizPreview, setBizPreview] = useState(null);
  var [ocrLoading, setOcrLoading] = useState(false);
  var [submitted, setSubmitted] = useState(false);
  var [inviteDone, setInviteDone] = useState(false);   // 초대 가입 완료 → 앱 설치 안내 화면 (Jin 10/29)
  // 대행사 거래처 초대 링크 (B4) — /register?invite=<token>. 공개 GET 으로 확인 후 업체명·사업자번호를 미리 채운다(수정 불가).
  //   초대 가입은 서버가 업체 유형·용도를 무시하고 말소 화면으로 고정 → 유형·용도 선택 숨김 · purpose 미전송 · inviteToken 전송.
  var [searchParams] = useSearchParams();
  var inviteToken = searchParams.get('invite') || '';
  var [invite, setInvite] = useState(null);   // null = 확인 전 · {valid:true, companyName, bizNo, agencyName} · {valid:false, reason} · {error}
  var invited = !!(invite && invite.valid);
  // 대행사 대표 링크 (YK 대표 링크 2026-11-03) — /register?share=<token>. 공개 GET 은 대행사명만 준다(업체 정보 미리 채움 없음).
  //   가입은 일반 가입처럼 오토덱스 승인 대기 + 말소 화면 고정 → 유형·용도 선택 숨김 · purpose 미전송 · shareToken 전송.
  //   대표 이름·연락처 필수(서버 FIELD_REQUIRED). 초대(?invite=)가 같이 오면 초대가 우선(서버와 같음).
  var shareToken = inviteToken ? '' : (searchParams.get('share') || '');
  var [share, setShare] = useState(null);     // null = 확인 전 · {valid:true, agencyName} · {valid:false, reason} · {error}
  var shared = !!(share && share.valid);
  var shareAgency = shared ? (share.agencyName || '') : '';
  var shareAgencyI = shareAgency + koJosa(shareAgency, '이', '가');

  useEffect(function() {
    if (!shareToken) return;
    fetch(API + '/public/agency-share/' + encodeURIComponent(shareToken))
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d && d.valid) setShare(d);
        else if (d && d.valid === false) setShare({ valid: false, reason: d.reason });
        else setShare({ error: (d && d.error) || '서버 연결 실패' });   // 429 limiter 바디엔 valid 키가 없다
      }).catch(function() { setShare({ error: '서버 연결 실패' }); });
  }, [shareToken]);

  useEffect(function() {
    if (!inviteToken) return;
    fetch(API + '/public/malso-invites/' + encodeURIComponent(inviteToken))
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d && d.valid) {
          setInvite(d);
          setForm(function(prev) { return Object.assign({}, prev, { companyName: d.companyName || '', bizNo: d.bizNo || '' }); });
        } else if (d && d.valid === false) {
          setInvite({ valid: false, reason: d.reason });
        } else {
          setInvite({ error: (d && d.error) || '서버 연결 실패' });   // 429 limiter 바디엔 valid 키가 없다
        }
      }).catch(function() { setInvite({ error: '서버 연결 실패' }); });
  }, [inviteToken]);

  var set = function(k, v) { var f = Object.assign({}, form); f[k] = v; setForm(f); if (k === 'userId') setIdCheck(null); };

  var getPasswordStrength = function(pw) {
    if (!pw) return { level: 0, label: '', color: '#e5e7eb' };
    var score = 0;
    if (pw.length >= 8) score++;
    if (/[a-zA-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pw)) score++;
    if (pw.length >= 12) score++;
    if (score <= 2) return { level: 1, label: '약함', color: '#ef4444' };
    if (score <= 3) return { level: 2, label: '보통', color: '#f59e0b' };
    return { level: 3, label: '강함', color: '#16a34a' };
  };
  var pwStrength = getPasswordStrength(form.password);
  // 서버 validatePassword(routes/auth.js:21)와 동일 조건 — 실시간 체크리스트 + submit 검증 단일 소스
  var pwConditions = {
    length: (form.password || '').length >= 8,
    letter: /[a-zA-Z]/.test(form.password || ''),
    number: /[0-9]/.test(form.password || ''),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(form.password || '')
  };
  var pwAllMet = pwConditions.length && pwConditions.letter && pwConditions.number && pwConditions.special;

  var handleBizFile = function(file) {
    if (!file) return;
    setBizFile(file);
    if (file.type.startsWith('image/')) {
      var reader = new FileReader();
      reader.onload = function(e) { setBizPreview(e.target.result); };
      reader.readAsDataURL(file);
    } else { setBizPreview(null); }
    // OCR 시도
    setOcrLoading(true);
    var fd = new FormData(); fd.append('image', file);
    fetch(API + '/auth/register-ocr', { method: 'POST', body: fd })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setOcrLoading(false);
        if (d.success && d.data) {
          var r = d.data;
          setForm(function(prev) { return Object.assign({}, prev, {
            companyName: r.name || prev.companyName,
            ceo: r.ceo || prev.ceo,
            bizNo: r.bizNo || prev.bizNo,
            tel: r.tel || prev.tel,
          }); });
        }
      }).catch(function() { setOcrLoading(false); });
  };

  var checkId = function() {
    if (!form.userId || form.userId.length < 3) { setIdCheck(false); return; }
    fetch(API + '/auth/check-id?userId=' + encodeURIComponent(form.userId))
      .then(function(r) { return r.json(); })
      .then(function(d) { setIdCheck(d.available); })
      .catch(function() { setIdCheck(null); });
  };

  var handleSubmit = function() {
    setError(''); setSuccess('');
    if (!form.userId || form.userId.length < 3) { setError('아이디는 3자 이상 입력해주세요.'); return; }
    if (!form.password || form.password.length < 8) { setError('비밀번호는 8자 이상 입력해주세요.'); return; }
    if (!/[a-zA-Z]/.test(form.password)) { setError('비밀번호에 영문자를 포함해주세요.'); return; }
    if (!/[0-9]/.test(form.password)) { setError('비밀번호에 숫자를 포함해주세요.'); return; }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(form.password)) { setError('비밀번호에 특수문자를 포함해주세요.'); return; }
    if (form.password !== form.passwordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
    if (!form.companyName) { setError('업체명을 입력해주세요.'); return; }
    if (shared && !form.ceo.trim()) { setError('대표 이름을 입력해주세요.'); return; }
    if (shared && !form.tel.trim()) { setError('연락처를 입력해주세요.'); return; }
    if (!invited && !shared && form.companyType === 'user' && !form.purpose) { setError('어떤 일로 쓰시는지 선택해주세요.'); return; }
    if (!agreeTerms || !agreePrivacy) { setError('이용약관 및 개인정보처리방침에 모두 동의해주세요.'); return; }
    if (honeypot) return; // bot trap
    setLoading(true);
    var fd = new FormData();
    fd.append('userId', form.userId.trim());
    fd.append('password', form.password);
    fd.append('companyName', form.companyName);
    fd.append('companyNameEn', form.companyNameEn);
    fd.append('ceo', form.ceo);
    fd.append('tel', form.tel);
    fd.append('bizNo', form.bizNo);
    fd.append('email', form.email);
    fd.append('companyType', form.companyType);
    if (form.companyType === 'user' && !invited && !shared) fd.append('purpose', form.purpose);
    if (invited) fd.append('inviteToken', inviteToken);
    if (shared) fd.append('shareToken', shareToken);
    if (bizFile) fd.append('bizLicense', bizFile);
    fetch(API + '/auth/register', { method: 'POST', body: fd })
    .then(function(r) { return r.json(); }).then(function(d) {
      if (d.success && d.invited) {
        // 초대 가입 = 바로 활성 · 자동 로그인 없음(user·token 미반환) — onLogin 분기보다 먼저 받는다.
        // 로그인 화면으로 바로 넘기지 않고 앱 설치 안내 + [웹으로 계속 보기] (Jin 10/29).
        setInviteDone(true);
        setLoading(false);
      } else if (d.success && d.pending) {
        // 승인 대기 모달 노출 + 폼 영구 잠금 (재클릭으로 인한 rate limit 방지)
        setSubmitted(true);
        setLoading(false);
      } else if (d.success) {
        if (d.token) localStorage.setItem('autodoc_token', d.token);
        setSuccess('가입 완료!');
        setTimeout(function() { onLogin(d.user); navigate('/main'); }, 1200);
      } else { setError(d.error || '가입 실패'); setLoading(false); }
    }).catch(function() { setError('서버 연결 실패'); setLoading(false); });
  };

  // 흰 바탕 + 구획 나눔 (앱 업체 회원가입 D안과 같은 모양, Jin 11/04) — 칸도 흰색, 구획 사이는 회색 띠
  var is = { width: '100%', height: 44, paddingLeft: 14, paddingRight: 14, border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', backgroundColor: T.card, fontFamily: "'Pretendard',sans-serif", boxSizing: 'border-box' };
  var lb = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 };
  var rw = { marginBottom: 14 };
  var hintSt = { fontSize: 11, color: T.muted, marginTop: 4, lineHeight: 1.45 };
  var srOnly = { position: 'absolute', opacity: 0, width: 1, height: 1, margin: 0, pointerEvents: 'none' };
  var section = function(title, body) {
    return (
      <section style={{ padding: '24px 0' }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: T.ink, margin: '0 0 14px' }}>{title}</h3>
        {body}
      </section>
    );
  };
  var band = <div style={{ height: 8, background: T.canvas, margin: '0 -24px' }} />;
  var shareIn = function(k, ph, type) {
    return <input value={form[k]} onChange={function(e) { set(k, e.target.value); }} placeholder={ph} type={type || 'text'} style={is} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />;
  };

  // 결과 화면(초대 가입 완료 · 가입 신청 완료 · 링크 안내) — 폼과 같은 흰 바탕·같은 폭, 가운데 정렬. 이모지 대신 선 아이콘 (Jin 11/04)
  var resultPage = function(icon, body) {
    return (
      <div style={{ minHeight: '100vh', background: T.card, fontFamily: "'Pretendard',sans-serif" }}>
        <div style={{ width: '100%', maxWidth: 520, margin: '0 auto', padding: '40px 24px 32px', boxSizing: 'border-box', textAlign: 'center' }}>
          <div style={{ marginBottom: 40 }}><LogoFull iconSize={36} fontSize={24} /></div>
          {icon}
          {body}
        </div>
      </div>
    );
  };
  var resultIcon = function(tone, path) {
    var c = tone === 'ok' ? [T.okTint, T.ok] : tone === 'accent' ? [T.accentTint, T.accent] : [T.bdSoft, T.muted];
    return (
      <span aria-hidden="true" style={{ width: 64, height: 64, borderRadius: '50%', background: c[0], display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={c[1]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{path}</svg>
      </span>
    );
  };
  var checkPath = <path d="M5 12.5l4.5 4.5L19 7.5" />;
  var linkPath = <React.Fragment><path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1.2 1.2" /><path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1.2-1.2" /></React.Fragment>;
  var resultTitle = { fontSize: 22, fontWeight: 800, color: T.ink, margin: '0 0 10px' };
  var resultText = { fontSize: 14, color: T.dim, lineHeight: 1.7, margin: 0, wordBreak: 'keep-all' };
  // 폼의 [가입하기] 와 같은 버튼 (a·button 공용)
  var primaryBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 48, padding: 0, border: 'none', borderRadius: 12, backgroundColor: T.ink, color: T.card, fontSize: 15, fontWeight: 700, textDecoration: 'none', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", boxSizing: 'border-box', boxShadow: '0 4px 12px rgba(15,23,42,0.2)' };

  // 초대 가입 완료 — 앱 설치 유도 + 웹으로 계속 (로그인 화면). 아이디를 다시 보여 줘 앱·웹 로그인에 그대로 쓰게.
  if (inviteDone) {
    // 휴대폰이면 그 기기 스토어 하나만, PC 는 둘 다
    var ua = navigator.userAgent || '';
    var isIos = /iPhone|iPad|iPod/i.test(ua), isAndroid = /Android/i.test(ua);
    return resultPage(resultIcon('ok', checkPath), (
      <div data-part="invite-done">
        <h2 style={resultTitle}>가입이 완료되었습니다</h2>
        <p style={resultText}>앱을 설치하면 휴대폰에서 바로 말소를 신청하고 진행 상황을 볼 수 있습니다.</p>
        <p style={{ ...resultText, marginTop: 6 }}>아이디 <strong style={{ color: T.ink }}>{form.userId.trim()}</strong> 로 로그인하세요.</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', margin: '28px 0 12px' }}>
          {!isAndroid && <StoreBadge kind="apple" href={APP_STORE_URL} light />}
          {!isIos && <StoreBadge kind="play" href={PLAY_STORE_URL} light />}
        </div>
        <button onClick={function() { navigate('/login'); }} style={{ ...primaryBtn, backgroundColor: T.card, color: T.accent, border: '1.5px solid ' + T.accentBd, boxShadow: 'none' }}>
          웹으로 계속 보기
        </button>
      </div>
    ));
  }

  // 가입 신청 완료(승인 대기) — 폼 자리를 바꿔 그려 다시 제출할 수 없게
  if (submitted) {
    return resultPage(resultIcon('accent', checkPath), (
      <React.Fragment>
        <h2 style={resultTitle}>가입 신청 완료</h2>
        <p style={resultText}>
          관리자 <strong style={{ color: T.accent }}>승인 대기 중</strong>입니다.<br />
          승인이 완료되면 {form.email ? <><strong style={{ color: T.ink }}>{form.email}</strong>로 </> : ''}안내드리며,<br />
          그 후 로그인하여 서비스를 이용하실 수 있습니다.
        </p>
        <p style={{ fontSize: 12, color: T.faint, margin: '14px 0 28px' }}>신청은 한 번이면 됩니다.</p>
        <button onClick={function() { navigate('/login'); }} style={primaryBtn}>로그인 페이지로</button>
      </React.Fragment>
    ));
  }

  // 초대 링크 확인 중 · 쓸 수 없는 초대 · 쓸 수 없는 대표 링크(404 not_found · 410 revoked) — 가입 폼 대신 안내만
  if ((inviteToken && !invited) || (shareToken && !shared)) {
    var usedInvite = !!invite && (invite.reason === 'already_used' || invite.reason === 'unit_unavailable');
    var checking = shareToken ? !share : !invite;
    var inviteMsg = shareToken
      ? (!share ? '가입 링크를 확인하는 중…'
        : share.reason ? '이 가입 링크는 더 이상 쓸 수 없습니다. 링크를 보낸 업체에 새 링크를 요청해 주세요.'
        : share.error)
      : !invite ? '초대 정보를 확인하는 중…'
      : usedInvite ? '이미 가입이 완료된 초대입니다. 로그인해 주세요.'
      : invite.reason ? '초대 링크가 만료되었거나 올바르지 않습니다. 대행사에 다시 요청해 주세요.'
      : invite.error;
    return resultPage(checking ? null : usedInvite ? resultIcon('ok', checkPath) : resultIcon('neutral', linkPath), (
      <React.Fragment>
        <p style={{ ...resultText, fontSize: 15, color: checking ? T.muted : T.ink }}>{inviteMsg}</p>
        {usedInvite && <a href="/login" style={{ ...primaryBtn, marginTop: 28 }}>로그인</a>}
      </React.Fragment>
    ));
  }

  return (
    <div style={{ minHeight: '100vh', background: T.card, fontFamily: "'Pretendard',sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 520, margin: '0 auto', padding: '40px 24px 32px', boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <LogoFull iconSize={36} fontSize={24} />
        </div>
        {/* 대행사가 보낸 초대(issuerName) = '오토상사 초대' + 작은 줄 '말소대행 · 와이케이글로벌' · YK 초대('') = 현행 */}
        {invited && (invite.issuerName || invite.agencyName) && <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 4 }}>{invite.issuerName || invite.agencyName} 초대</div>}
        {invited && invite.issuerName && invite.agencyName && <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>말소대행 · {invite.agencyName}</div>}
        {shared && <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 4 }}>{shareAgency} 말소대행 가입</div>}
        {shared && <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>{shareAgencyI} 보낸 링크입니다</div>}
        <h2 style={{ fontSize: 22, fontWeight: 800, color: T.ink, margin: '0 0 4px' }}>회원가입</h2>
        <p style={{ fontSize: 13, color: T.muted, margin: '0 0 8px' }}>{invited ? '차량번호만으로 말소대행을 신청합니다.'
          : shared ? '오토덱스가 가입을 승인하고 ' + shareAgencyI + ' 거래처 연결을 확인하면 말소를 신청하실 수 있습니다.'
          : '가입 후 관리자 승인이 완료되면 바로 이용할 수 있습니다'}</p>

        {error && <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginTop: 16, color: '#dc2626', fontSize: 13, fontWeight: 500 }}>{error}</div>}
        {success && <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginTop: 16, color: '#16a34a', fontSize: 13, fontWeight: 500 }}>{success}</div>}

        {/* 대표 링크 가입 — 시안 YkShareJoin: '우리 업체' 4칸(업체명·사업자번호·대표 이름·연락처) 먼저, 그다음 '로그인 정보'. 일반 업체 칸은 아래에서 숨긴다. */}
        {shared && (<React.Fragment>
          {section('우리 업체', <React.Fragment>
            <div style={rw}>
              <label style={lb}>업체명 *</label>
              {shareIn('companyName', '(주)무역상사')}
              <div style={hintSt}>{shareAgency}에 알려 주신 업체 이름으로 적어 주세요.</div>
            </div>
            <div style={rw}>
              <label style={lb}>사업자등록번호 <span style={{ color: T.faint, fontWeight: 400 }}>(선택)</span></label>
              {shareIn('bizNo', '000-00-00000')}
              <div style={hintSt}>적으시면 거래처를 더 정확히 찾습니다.</div>
            </div>
            <div style={rw}>
              <label style={lb}>대표 이름 *</label>
              {shareIn('ceo', '홍길동')}
            </div>
            <div style={{ ...rw, marginBottom: 0 }}>
              <label style={lb}>연락처 *</label>
              {shareIn('tel', '010-1234-5678', 'tel')}
              <div style={hintSt}>오토덱스 승인과 {shareAgency} 연결 확인 때 봅니다.</div>
            </div>
          </React.Fragment>)}
          {band}
        </React.Fragment>)}

        {section('로그인 정보', <React.Fragment>
              <div style={rw}>
                <label style={lb}>아이디 *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={form.userId} onChange={function(e) { set('userId', e.target.value.replace(/[^a-zA-Z0-9_]/g, '')); }} placeholder="영문/숫자 3자 이상" style={{ ...is, flex: 1 }} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
                  <button onClick={checkId} style={{ padding: '0 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: '#f8fafc', color: '#475569', whiteSpace: 'nowrap' }}>중복확인</button>
                </div>
                {idCheck === true && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>사용 가능한 아이디입니다.</div>}
                {idCheck === false && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>사용할 수 없는 아이디입니다.</div>}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ ...rw, flex: 1 }}>
                  <label style={lb}>비밀번호 *</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPw ? 'text' : 'password'} value={form.password} onChange={function(e) { set('password', e.target.value); }} placeholder="8자 이상 (영문+숫자+특수)" style={{ ...is, paddingRight: 38 }} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
                    <button type="button" aria-label={showPw ? '비밀번호 숨기기' : '비밀번호 보기'} onClick={function() { setShowPw(!showPw); }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, display: 'flex' }}><PwEyeIcon open={!showPw} /></button>
                  </div>
                  {form.password && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <div style={{ flex: 1, height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: (pwStrength.level / 3 * 100) + '%', background: pwStrength.color, borderRadius: 2, transition: 'all 0.2s' }} />
                    </div>
                    <span style={{ fontSize: 10, color: pwStrength.color, fontWeight: 600, minWidth: 28 }}>{pwStrength.label}</span>
                  </div>}
                </div>
                <div style={{ ...rw, flex: 1 }}>
                  <label style={lb}>비밀번호 확인 *</label>
                  <input type="password" value={form.passwordConfirm} onChange={function(e) { set('passwordConfirm', e.target.value); }} placeholder="비밀번호 재입력" style={is} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
                  {form.passwordConfirm && form.password !== form.passwordConfirm && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>비밀번호 불일치</div>}
                </div>
              </div>

              {/* 비밀번호 조건 실시간 체크리스트 — 서버 validatePassword와 동일 조건 */}
              <div style={{ background: pwAllMet ? '#f0fdf4' : '#f8fafc', border: '1px solid ' + (pwAllMet ? '#bbf7d0' : '#e2e8f0'), borderRadius: 10, padding: '10px 14px', marginBottom: 0, fontSize: 12, transition: 'all 0.2s' }}>
                <div style={{ fontWeight: 600, color: '#475569', marginBottom: 6, fontSize: 11 }}>비밀번호 조건</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                  {[
                    { key: 'length', label: '8자 이상' },
                    { key: 'letter', label: '영문 포함' },
                    { key: 'number', label: '숫자 포함' },
                    { key: 'special', label: '특수문자 포함' }
                  ].map(function(c) {
                    var ok = pwConditions[c.key];
                    return (
                      <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, color: ok ? '#16a34a' : '#94a3b8', fontWeight: ok ? 600 : 400 }}>
                        <span style={{ width: 16, height: 16, borderRadius: '50%', background: ok ? '#16a34a' : '#e2e8f0', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0 }}>{ok ? '✓' : '·'}</span>
                        <span>{c.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
        </React.Fragment>)}

        {!invited && !shared && (<React.Fragment>
          {band}
          {section('어떤 업체인가요?', <React.Fragment>
            {/* 업체 유형 — 가로 버튼 3개 */}
            <div role="radiogroup" aria-label="업체 유형" style={{ display: 'flex', gap: 8 }}>
              {[
                { value: 'user', label: '수출업체 (일반)' },
                { value: 'doc_agency', label: '서류대행업체' },
                { value: 'cancel_agency', label: '말소대행업체' },
              ].map(function(t) {
                var sel = form.companyType === t.value;
                return (
                  <label key={t.value} style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '8px 6px', boxSizing: 'border-box', borderRadius: 10, cursor: 'pointer', border: '1.5px solid ' + (sel ? T.accent : T.bd), background: sel ? T.accentTint : T.card, fontSize: 13, fontWeight: sel ? 700 : 500, color: sel ? T.accent : T.dim, wordBreak: 'keep-all', transition: 'all 0.15s' }}>
                    <input type="radio" name="companyType" value={t.value} checked={sel} onChange={function() { set('companyType', t.value); }} style={srOnly} />
                    <span>{t.label}</span>
                  </label>
                );
              })}
            </div>

            {/* 쓰는 용도 — 구분선 목록, 고른 줄은 파란 글자 + 체크 */}
            {form.companyType === 'user' && (
              <div style={{ marginTop: 20 }}>
                <label style={lb}>어떤 일로 쓰시나요? *</label>
                <div style={{ borderTop: '1px solid ' + T.bdSoft }}>
                  {[
                    { value: 'malso', label: '말소대행신청', sub: '차량번호만으로 말소대행을 신청합니다' },
                    { value: 'eval', label: '차량 평가 신청', sub: '평가사에게 검차를 요청하고 결과를 받습니다' },
                    { value: 'full', label: '수출 업무 전체 관리', sub: '차량·인보이스·세무·장부까지 ERP 전체' },
                  ].map(function(t) {
                    var sel = form.purpose === t.value;
                    return (
                      <label key={t.value} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 2px', borderBottom: '1px solid ' + T.bdSoft, cursor: 'pointer' }}>
                        <input type="radio" name="purpose" value={t.value} checked={sel} onChange={function() { set('purpose', t.value); }} style={srOnly} />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 14, fontWeight: sel ? 700 : 500, color: sel ? T.accent : T.ink }}>{t.label}</span>
                          <span style={{ fontSize: 12, color: T.muted }}>{t.sub}</span>
                        </span>
                        {sel && <span aria-hidden="true" style={{ color: T.accent, fontSize: 16, fontWeight: 800 }}>✓</span>}
                      </label>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: T.faint, marginTop: 8 }}>나중에 더보기에서 바꿀 수 있습니다</div>
              </div>
            )}
          </React.Fragment>)}
        </React.Fragment>)}

        {/* 일반·초대 가입의 업체 칸 — 대표 링크 가입은 위 '우리 업체' 4칸만 */}
        {!shared && (<React.Fragment>
          {band}
          {section('업체 정보', <React.Fragment>
            {/* 사업자등록증 — 자동으로 채워질 칸 바로 위 (앱과 같은 자리) */}
            <div role="button" tabIndex={0} aria-label="사업자등록증 올리기"
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', marginBottom: 18, borderRadius: 12, cursor: 'pointer', background: bizFile ? T.okTint : T.accentTint, border: '1.5px dashed ' + (bizFile ? T.okBd : T.accentBd), transition: 'all 0.15s' }}
              onClick={function() { document.getElementById('bizLicenseInput').click(); }}
              onKeyDown={function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); document.getElementById('bizLicenseInput').click(); } }}
              onDragOver={function(e) { e.preventDefault(); e.currentTarget.style.borderColor = T.accent; }}
              onDragLeave={function(e) { e.currentTarget.style.borderColor = bizFile ? T.okBd : T.accentBd; }}
              onDrop={function(e) { e.preventDefault(); e.currentTarget.style.borderColor = bizFile ? T.okBd : T.accentBd; if (e.dataTransfer.files[0]) handleBizFile(e.dataTransfer.files[0]); }}>
              <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, background: bizFile ? T.ok : T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {bizFile
                  ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.card} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>
                  : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.card} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>사업자등록증 올리고 빠르게 입력</span>
                <span style={{ fontSize: 12, color: ocrLoading ? T.accent : bizFile ? T.ok : T.dim, fontWeight: (ocrLoading || bizFile) ? 600 : 400, wordBreak: bizFile ? 'break-all' : 'keep-all' }}>
                  {ocrLoading ? 'AI 분석 중...'
                    : bizFile ? bizFile.name + ' (' + (bizFile.size / 1024).toFixed(0) + 'KB)'
                    : '업체명·대표자·번호를 자동으로 채워요 (선택) · JPG, PNG, PDF'}
                </span>
              </span>
              <input id="bizLicenseInput" type="file" accept="image/jpeg,image/png,application/pdf" style={{ display: 'none' }} onChange={function(e) { if (e.target.files[0]) handleBizFile(e.target.files[0]); e.target.value = ''; }} />
            </div>
            {bizPreview && <img src={bizPreview} alt="미리보기" style={{ display: 'block', margin: '-6px 0 18px', maxHeight: 120, borderRadius: 8, border: '1px solid ' + T.bd }} />}

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...rw, flex: 1, minWidth: 0 }}>
                <label style={lb}>업체명 (한글) *</label>
                <input value={form.companyName} onChange={function(e) { set('companyName', e.target.value); }} placeholder="(주)무역상사" style={is} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
              </div>
              <div style={{ ...rw, flex: 1, minWidth: 0 }}>
                <label style={lb}>업체명 (영문)</label>
                <input value={form.companyNameEn} onChange={function(e) { set('companyNameEn', e.target.value); }} placeholder="Trading Co." style={is} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...rw, flex: 1, minWidth: 0, marginBottom: 0 }}>
                <label style={lb}>대표자명</label>
                <input value={form.ceo} onChange={function(e) { set('ceo', e.target.value); }} placeholder="홍길동" style={is} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
              </div>
              <div style={{ ...rw, flex: 1, minWidth: 0, marginBottom: 0 }}>
                <label style={lb}>사업자등록번호 <span style={{ color: '#94a3b8', fontWeight: 400 }}>(선택)</span></label>
                <input value={form.bizNo} onChange={function(e) { set('bizNo', e.target.value); }} placeholder="000-00-00000" style={is} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
              </div>
            </div>
          </React.Fragment>)}
          {band}
          {section('연락처', <React.Fragment>
            <div style={rw}>
              <label style={lb}>전화번호</label>
              <input value={form.tel} onChange={function(e) { set('tel', e.target.value); }} placeholder="010-1234-5678" type="tel" style={is} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
            </div>
            <div style={{ ...rw, marginBottom: 0 }}>
              <label style={lb}>이메일 <span style={{ color: '#94a3b8', fontWeight: 400 }}>{invited ? '(선택)' : '(승인 알림 수신용)'}</span></label>
              <input type="email" value={form.email} onChange={function(e) { set('email', e.target.value); }} placeholder="example@company.com" style={is} onFocus={function(e) { e.target.style.borderColor = '#3b82f6'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e8f0'; }} />
            </div>
          </React.Fragment>)}
        </React.Fragment>)}

        {/* honeypot — hidden from humans */}
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
          <input value={honeypot} onChange={function(e) { setHoneypot(e.target.value); }} tabIndex={-1} autoComplete="off" />
        </div>

        {band}
        <div style={{ paddingTop: 24 }}>
          <div style={{ ...rw, marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <input type="checkbox" checked={agreeTerms} onChange={function(e) { setAgreeTerms(e.target.checked); }} id="ck-terms" style={{ flexShrink: 0 }} />
              <label htmlFor="ck-terms" style={{ fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                <a href="/terms" target="_blank" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>이용약관</a>에 동의합니다 <span style={{ color: '#dc2626', fontSize: 11 }}>(필수)</span>
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={agreePrivacy} onChange={function(e) { setAgreePrivacy(e.target.checked); }} id="ck-privacy" style={{ flexShrink: 0 }} />
              <label htmlFor="ck-privacy" style={{ fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                <a href="/privacy" target="_blank" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>개인정보처리방침</a>에 동의합니다 <span style={{ color: '#dc2626', fontSize: 11 }}>(필수)</span>
              </label>
            </div>
          </div>

          <button onClick={handleSubmit} disabled={loading} style={{ width: '100%', height: 48, backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', fontFamily: "'Pretendard',sans-serif", boxShadow: '0 4px 12px rgba(15,23,42,0.2)', opacity: loading ? 0.85 : 1, marginTop: 4 }}
            onMouseEnter={function(e) { if (!loading) { e.target.style.backgroundColor = '#2563eb'; e.target.style.boxShadow = '0 4px 16px rgba(37,99,235,0.3)'; } }}
            onMouseLeave={function(e) { e.target.style.backgroundColor = '#0f172a'; e.target.style.boxShadow = '0 4px 12px rgba(15,23,42,0.2)'; }}
          >{loading ? '가입 처리 중...' : (shared ? '가입하기' : '무료로 시작하기')}</button>

          {!invited && !shared && (
            <div style={{ background: '#f0f9ff', borderRadius: 10, padding: '10px 14px', marginTop: 16, fontSize: 12, color: '#0369a1', lineHeight: 1.6 }}>
              <strong>승인 후 바로 이용:</strong> OCR 스캔 100건/월, 서류 자동생성, 차량 관리, 인보이스 발행
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <span style={{ color: '#94a3b8', fontSize: 14 }}>이미 계정이 있으신가요? </span>
          <a href="/login" style={{ color: '#2563eb', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>로그인 →</a>
        </div>
        <div style={{ textAlign: 'center', marginTop: 32 }}><p style={{ color: '#cbd5e1', fontSize: 12 }}>© 2026 AUTODEX. All rights reserved.</p></div>
      </div>
    </div>
  );
}

export default Register;
